/**
 * Pure helpers that decide whether Save / Publish / Duplicate actions are
 * currently allowed. Extracted into its own file so tests can exercise the
 * decisions without mounting React, and so the UI doesn't scatter the
 * logic across multiple components (where it was drifting — Publish in
 * ChatPane had one rule, Publish in AppsPage had another).
 *
 * Each gate returns a `{ allowed, reason }` tuple. `reason` is a short
 * user-facing string the UI can surface as a disabled-button tooltip —
 * "Wait for the current response to finish" is a better hint than a
 * silent grey-out.
 *
 * The `state` argument is a plain object — the UI doesn't have to be
 * careful to pass a stable React state. Missing fields default to false.
 */

/**
 * True when any AI/deployment work is in flight. We deliberately do NOT
 * include `pendingFixPlan` here — that's a user decision point (the user
 * is reviewing a plan), not a background process. Blocking save/publish
 * while the user decides would be wrong.
 */
export function isAIWorking(state = {}) {
  return Boolean(
    state.loading ||       // chat stream in progress
    state.deploying ||     // save/promote in progress
    state.validating ||    // post-deploy validation running
    state.autoFixing       // auto-fix iteration in flight
  );
}

/**
 * Can the user save the pending payload? Only if there's a payload to
 * save AND no AI work is in flight.
 */
export function canSave(state = {}) {
  if (!state.hasPendingPayload) {
    return { allowed: false, reason: "Nothing to save." };
  }
  if (state.isSaved) {
    return { allowed: false, reason: "Already saved." };
  }
  if (isAIWorking(state)) {
    return { allowed: false, reason: saveBusyReason(state) };
  }
  return { allowed: true };
}

/**
 * Can the user publish? Rules:
 *   - Must have a deployed app (or the current payload must be saved)
 *   - No AI work can be in flight (publish while code is mutating is a race)
 *   - If there's a pending UNSAVED payload, publish is blocked with a
 *     "save first" message — we won't silently publish the stale last-saved
 *     version and surprise the user.
 */
export function canPublish(state = {}) {
  if (isAIWorking(state)) {
    return { allowed: false, reason: saveBusyReason(state) };
  }
  if (state.hasPendingPayload && !state.isSaved) {
    return {
      allowed: false,
      reason: "You have unsaved changes. Save them first (or discard) before publishing.",
    };
  }
  // No pending changes, or the pending payload is saved — OK to publish.
  return { allowed: true };
}

/**
 * Can the user duplicate this app? Only block if we're mid-generation for
 * THIS app — duplicating someone else's app while this chat streams is
 * fine. A name-collision check happens server-side.
 */
export function canDuplicate(state = {}) {
  if (isAIWorking(state)) {
    return { allowed: false, reason: saveBusyReason(state) };
  }
  if (!state.sourceAppName) {
    return { allowed: false, reason: "Select an app to duplicate." };
  }
  return { allowed: true };
}

function saveBusyReason(state) {
  if (state.loading) return "Wait for the current response to finish.";
  if (state.deploying) return "Wait for the save to finish.";
  if (state.autoFixing) return "Auto-fix is running — please wait.";
  if (state.validating) return "Validating the last deploy — please wait.";
  return "Please wait for the current operation to finish.";
}
